/**
 * Eric 2026-06-25 (iter125 — Session A11) — Header de VideoDetailPage.
 *
 * Extrait de VideoDetailPage.js (-313 lignes). Contient :
 *   - Bouton retour, titre + édition inline, métadonnées (durée, source),
 *     bandeau pathway, actions (Partager, Promouvoir, Importer, Supprimer),
 *     JargonTermsPanel inline.
 *
 * Gating canEdit/isAdmin côté parent.
 */
import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { cachedGet, invalidateCache } from "../../lib/apiCache";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, BookOpen, CaretDown, CaretLeft, CaretRight,
  CheckCircle, CircleNotch, TextAa, FileText,
  FolderSimple, Path, PencilSimple, PlayCircle, ShareNetwork, Tag, Tree, Trash,
  UploadSimple, VideoCamera, Warning, Heart,
  DownloadSimple,
} from "@phosphor-icons/react";
import PromoteVideoButton from "../PromoteVideoButton";
import { canAccessAdmin } from "../../utils/permissions";
import { trainingThemeLabel } from "../../lib/trainingThemes";
import { matchesPathwayQuery } from "../../lib/pathwayFilter";
import { useCollapsedPathGroups } from "../../lib/useCollapsedPathGroups"; // Tâche 1166
import { useRecentPathways } from "../../lib/useRecentPathways"; // Tâche 1168

export const VideoDetailHeader = (props) => {
  // Tâches 1355 · 1356 (Eric 2026-06-30) — Les libellés des chips
  // (« THÈME », « PARCOURS », « ÉTIQUETTES »…) et « Sauvegarder » restaient
  // en français dans la version anglaise. Seules les ÉTIQUETTES sont
  // traduites : les VALEURS (thèmes de formation) restent en français,
  // décision B629 de Vincent.
  const tr = (fr, en) => (props.lang === "fr" ? fr : en);
  const {
    lang, api, video, videoId, canEdit, isAdmin,
    isComplete, isFailed, isPaused,
    user, data,
    goBack,
    // Vincent B3 XLS 2026-08-10 — Nav Prev/Next entre vidéos
    goToPrevVideo, goToNextVideo, videoNavLabel,
    // Tâches 1465/1466/1469 — un fichier texte n'est pas une vidéo.
    isTextDoc = false,
    telechargementBloque = false,
    peutReglerTelechargement = false,
    onBasculerTelechargement,
    // Vincent B718 bonus (Eric 2026-02-11) — Titres du fichier précédent
    // et suivant, injectés en tooltip sur les boutons Prev/Next.
    prevVideoTitle, nextVideoTitle,
    editMode, setEditMode, editSaving,
    editTitle, setEditTitle,
    editTheme, setEditTheme,
    editAccessLevel, setEditAccessLevel,
    editDropbox, setEditDropbox,
    // Vincent (2026-02) bugs #524/#525
    categories = [],
    allPathways = [],
    editCategoryId, setEditCategoryId,
    editSelectedPathwayIds, setEditSelectedPathwayIds,
    startEdit, saveEdit,
    openShareModal, setImportOpen, setShowDeleteConfirm,
    pathwaySuggestions, pathwayAdding, setPathwayAdding,
    pathwaySelected, setPathwaySelected,
    pathwayBannerDismissed, setPathwayBannerDismissed,
    fmtDur, loadVideoData,
    // Vincent B923 (Eric 2026-02-14 SPEC STRICK PROD) — Ouverture du
    // dialog de gestion des étiquettes (manuel + suggestion auto).
    // Callback géré par le parent VideoDetailPage qui monte le Dialog.
    openTagsManager,
    // Tâche 1310 (Eric 2026-06-30) — Source canonique (« Whisper »,
    // « Fireflies » ou « fusionnée »), calculée par VideoDetailPage qui a
    // les trois transcriptions sous la main.
    canonicalSourceLabel = "",
  } = props;
  // Vincent #525 — État local du menu déroulant "checklist parcours".
  const [pathwayMenuOpen, setPathwayMenuOpen] = useState(false);
  // Tâches 1157 et 1166 — Familles de parcours repliées, mémorisées d'un
  // fichier au suivant (hook partagé avec l'explorateur).
  const { collapsed: collapsedPathGroups, toggleGroup: togglePathGroup } =
    useCollapsedPathGroups();
  // Tâche 1164 (Eric 2026-06-17) — Chercher plutôt que dérouler : à trente
  // parcours, la liste devient un couloir.
  const [pathwayQuery, setPathwayQuery] = useState("");
  // Tâche 1170 — Correction des majuscules du titre.
  const [fixingTitle, setFixingTitle] = useState(false);

  const fixTitleCase = async () => {
    setFixingTitle(true);
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${video.id}/fix-title-case`,
        {},
        { withCredentials: true, timeout: 120000 },
      );
      if (data?.changed) {
        toast.success(`« ${data.previous_title} » → « ${data.title} »`);
        loadVideoData?.();
      } else {
        toast.info(lang === "fr"
          ? "La casse du titre est déjà correcte"
          : "Title capitalisation is already correct");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setFixingTitle(false);
    }
  };
  // Tâche 1168 — Les derniers parcours cochés remontent en tête du menu.
  const { recentIds, remember: rememberPathway } = useRecentPathways();
  const togglePathway = (pid) => {
    if (!setEditSelectedPathwayIds) return;
    setEditSelectedPathwayIds((prev) => {
      const n = new Set(prev);
      if (n.has(pid)) n.delete(pid);
      else {
        n.add(pid);
        // On ne retient que l'ajout : décocher n'est pas « utiliser ».
        rememberPathway(pid);
      }
      return n;
    });
  };
  // Tâche 1157 (Eric 2026-06-17) — Ordre alphabétique dans les trois menus de
  // l'édition (thème, catégorie, parcours) : chercher « Stage FLHQ : 4 » dans
  // une liste rangée par ordre de création fait perdre du temps à chaque
  // fichier. Et un triangle par famille de parcours, parce que trois listes
  // dépliées d'un coup font défiler le menu sans fin.
  const byName = (a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""), "fr", { sensitivity: "base" });
  const filteredPaths = (ptype) => (allPathways || [])
    .filter((p) => p.pathway_type === ptype)
    .filter((p) => matchesPathwayQuery(p, pathwayQuery))
    .sort(byName);
  const onboardingPaths = filteredPaths("onboarding");
  // L'ordre d'usage prime ici : trier par nom ferait perdre le « dernier
  // utilisé », qui est toute l'utilité de la section.
  const recentPaths = recentIds
    .map((id) => (allPathways || []).find((p) => p.id === id))
    .filter(Boolean)
    .filter((p) => matchesPathwayQuery(p, pathwayQuery));
  const weeklyPaths = filteredPaths("weekly");
  // Vincent B652 (Eric 2026-08-05 SPEC STRICK PROD) — 3e type de parcours
  // « weekly » (Formations hebdomadaires). Ne PLUS mettre les weekly dans
  // learningPaths via le fallback `!== "onboarding"` : les 3 groupes sont
  // désormais distincts.
  const learningPaths = filteredPaths("learning");
  const selectedCount = editSelectedPathwayIds ? editSelectedPathwayIds.size : 0;

  // Vincent (2026-02, follow-up bugs #524/#525) — Détection de modifications
  // en cours dans le formulaire d'édition. Sert à afficher un badge "Modifié"
  // discret à côté du bouton Sauvegarder pour que l'utilisateur voie tout de
  // suite si le clic va vraiment sauver quelque chose (utile quand il édite
  // juste le titre ou juste les parcours par ex.).
  const originalPathwayIds = useMemo(() => {
    const s = new Set();
    (allPathways || []).forEach((p) => {
      if ((p.video_ids || []).includes(videoId)) s.add(p.id);
    });
    return s;
  }, [allPathways, videoId]);
  const pathwaysChanged = useMemo(() => {
    const cur = editSelectedPathwayIds || new Set();
    if (cur.size !== originalPathwayIds.size) return true;
    for (const pid of cur) if (!originalPathwayIds.has(pid)) return true;
    return false;
  }, [editSelectedPathwayIds, originalPathwayIds]);
  const hasEditChanges = editMode && (
    (editTitle || "") !== (video?.title || "")
    || (editDropbox || "") !== (video?.dropbox_url || "")
    || (editCategoryId || "") !== (video?.category_id || "")
    || (editTheme || "") !== (video?.training_theme || "")
    || (editAccessLevel || 1) !== (video?.required_access_level || 1)
    || pathwaysChanged
  );

  // Vincent B712 (Eric 2026-02-11) — Bouton cœur favori à GAUCHE du titre
  // dans la fiche vidéo/texte ouverte. Vincent : « à gauche du titre (de
  // cette vidéo ou ce texte) un cœur, que l'on peut cliquer pour le
  // sélectionner ou le désélectionner ; quand il est sélectionné, le fichier
  // est ajouté aux favoris. » Text-docs sont stockés dans la même collection
  // que les vidéos → type "video" pour l'API /favorites (comme dans
  // FileExplorerPage, cf. B588).
  const [isFavorite, setIsFavorite] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  // Vincent B712+ (Eric 2026-02-11) — Statistiques sociales du favori :
  // nombre total d'utilisateurs de l'org qui l'ont favorité + flag popularité.
  // Cœur BLEU si is_popular (>= 3 favoris dans l'org).
  const [favStats, setFavStats] = useState({ count: 0, is_popular: false, threshold: 3 });
  useEffect(() => {
    if (!videoId) return;
    let cancelled = false;
    (async () => {
      try {
        const [{ data: favs }, { data: stats }] = await Promise.all([
          cachedGet(axios, `${api}/api/favorites`, { ttl: 30000 }),
          axios.get(`${api}/api/favorites/stats/video/${videoId}`, { withCredentials: true }),
        ]);
        const list = Array.isArray(favs) ? favs : [];
        if (!cancelled) {
          setIsFavorite(list.some((f) => f.item_type === "video" && f.item_id === videoId));
          setFavStats(stats || { count: 0, is_popular: false, threshold: 3 });
        }
      } catch (_) { /* silencieux */ }
    })();
    return () => { cancelled = true; };
  }, [videoId, api]);
  const refreshFavStats = async () => {
    try {
      const { data } = await axios.get(`${api}/api/favorites/stats/video/${videoId}`, { withCredentials: true });
      setFavStats(data || { count: 0, is_popular: false, threshold: 3 });
    } catch (_) { /* silencieux */ }
  };
  const toggleFavorite = async () => {
    if (!videoId || favLoading) return;
    setFavLoading(true);
    try {
      if (isFavorite) {
        await axios.delete(`${api}/api/favorites/video/${videoId}`, { withCredentials: true });
        setIsFavorite(false);
      } else {
        await axios.post(
          `${api}/api/favorites`,
          { type: "video", item_id: videoId },
          { withCredentials: true }
        );
        setIsFavorite(true);
      }
      try { window.dispatchEvent(new CustomEvent("favorites:updated")); } catch (_) { /* silencieux */ }
      invalidateCache("/api/favorites");
      // Rafraîchit les stats après toggle (peut changer le flag popular).
      refreshFavStats();
    } catch (err) {
      toast.error(
        (lang === "fr" ? "Erreur favori : " : "Favorite error: ") +
        (err?.response?.data?.detail || err.message)
      );
    } finally {
      setFavLoading(false);
    }
  };

  return (
    <>
      {/* Vincent B725 v2 (Eric 2026-02-13) — Structure en 2 lignes :
          - Ligne 1 : TOUS les boutons dans l'ordre demandé
            [Retour] [Précédent] [X/Max] [Suivant] [Importer] [Partager] [Promote] [Dropbox]
          - Ligne 2 : Informations du fichier (titre, chips métadonnées)
          Objectif : les boutons ne se déplacent PLUS verticalement quand on
          navigue Prev/Next entre fichiers de longueurs différentes. */}
      <div className="mb-4">
        <div
          className="flex items-center gap-2 mb-3 overflow-x-auto"
          data-testid="video-detail-toolbar"
          style={{ scrollbarWidth: "thin" }}
        >
        {/* Vincent B725 v4 (Eric 2026-02-14) — Nouveau layout Row 1 :
            [Retour orange] [Importer vert] [Partager bleu] ····· [Prev X/Max Suivant]
            avec hauteur STRICTEMENT uniforme (px-3 py-1.5 icon=14). Le
            spacer flex-1 pousse le nav Prev/Next tout à droite. */}
        <button
          onClick={goBack}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-transparent bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold transition-colors flex-shrink-0 shadow-sm"
          data-testid="back-btn"
          title={lang === "fr" ? "Retour à la page précédente" : "Back to previous page"}
        >
          <CaretLeft size={14} weight="bold" />
          {lang === "fr" ? "Retour" : "Back"}
        </button>
        {/* Importer fichier — vert (teal)
            Vincent B725 v5 (Eric 2026-02-14) — Retrait du gate `isComplete` :
            l'admin doit pouvoir importer FAQ/procédures/scripts pour une vidéo
            déjà uploadée même si le pipeline IA n'est pas encore terminé (ex.
            « 3. Rendez-vous initiaux – 6. RDV assurance + close » dont le
            pipeline_status peut ne pas être "complete"). */}
        {isAdmin && (
          <button
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-transparent bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-sm whitespace-nowrap flex-shrink-0"
            data-testid="header-import-file-btn"
            title={lang === "fr"
              ? "Importer un fichier (FAQ Google Sheet, procédures, scripts, etc.) pour cette vidéo"
              : "Upload a file (Google Sheet FAQ, procedures, scripts, etc.) for this video"}
          >
            <UploadSimple size={14} weight="bold" />
            {lang === "fr" ? "Importer fichier" : "Import file"}
          </button>
        )}
        {/* Partager — bleu
            Vincent B725 v5 (Eric 2026-02-14) — Retrait du gate `isComplete` :
            le partage de la vidéo par lien token ne dépend pas du pipeline IA. */}
        {video && (
          <button
            onClick={openShareModal}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-transparent bg-[#002FA7] hover:bg-[#001a5c] text-white rounded-lg text-xs font-semibold transition-colors shadow-sm whitespace-nowrap flex-shrink-0"
            data-testid="share-video-btn"
            title={lang === "fr" ? "Générer un lien de partage protege par token (expire 24h–30j)" : "Generate a token-protected share link (expires 24h–30d)"}
          >
            <ShareNetwork size={14} weight="bold" />
            {lang === "fr" ? "Partager" : "Share"}
          </button>
        )}
        {video && (
          <PromoteVideoButton video={video} onPromoted={() => loadVideoData()} />
        )}
        {video && video.dropbox_url && (
          <a
            href={video.dropbox_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-teal-200 bg-teal-50 text-teal-700 rounded-lg text-xs font-semibold hover:bg-teal-100 transition-colors whitespace-nowrap flex-shrink-0"
            data-testid="open-dropbox-btn"
            title={lang === "fr" ? "Lecture depuis Dropbox (source officielle FLHQ)" : "Play from Dropbox (official FLHQ source)"}
          >
            <VideoCamera size={14} weight="bold" />
            Dropbox
          </a>
        )}
        {/* Tâche 1469 (Eric 2026-06) — Télécharger le fichier d'origine.
            ⚠️ Rien à voir avec l'interdiction de télécharger les VIDÉOS
            (décision B725, garde-fou serveur compris) : un document texte
            arrive d'un fichier que son auteur possède déjà. Autorisé par
            défaut, comme demandé, et la case n'apparaît qu'aux rôles qui ont
            le droit de la décocher — la montrer grisée aux autres suggérerait
            un pouvoir qu'ils n'ont pas. */}
        {isTextDoc && video?.source_kind !== "url" && (
          <>
            {!telechargementBloque && (
              <a
                href={`${api}/api/text-documents/${videoId}/download`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-300 bg-white text-zinc-700 rounded-lg text-xs font-semibold hover:bg-zinc-50 transition-colors whitespace-nowrap flex-shrink-0"
                data-testid="text-doc-download-btn"
                title={lang === "fr"
                  ? "Télécharge le fichier d'origine, tel qu'il a été déposé."
                  : "Downloads the original file, exactly as uploaded."}
              >
                <DownloadSimple size={14} weight="bold" />
                {lang === "fr" ? "Télécharger" : "Download"}
              </a>
            )}
            {peutReglerTelechargement && (
              <label
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-zinc-600 cursor-pointer whitespace-nowrap flex-shrink-0"
                data-testid="text-doc-download-allow-label"
                title={lang === "fr"
                  ? "Décocher empêche le téléchargement de CE document, quel que soit le réglage de l'organisation."
                  : "Unticking blocks downloading THIS document, whatever the organisation setting."}
              >
                <input
                  type="checkbox"
                  checked={!telechargementBloque}
                  onChange={() => onBasculerTelechargement && onBasculerTelechargement(!telechargementBloque)}
                  className="accent-[#002FA7]"
                  data-testid="text-doc-download-allow"
                />
                {lang === "fr" ? "téléchargement permis" : "download allowed"}
              </label>
            )}
          </>
        )}
        {/* Spacer flex-1 — pousse le nav Prev/Next à droite */}
        <div className="flex-1 min-w-[8px]" data-testid="video-detail-toolbar-spacer" />
        {/* Prev/Next tout à droite */}
        {(goToPrevVideo || goToNextVideo) && (
          <div
            className="flex items-center gap-2 flex-shrink-0"
            data-testid="video-detail-prev-next-nav"
          >
            <button
              type="button"
              onClick={goToPrevVideo || undefined}
              disabled={!goToPrevVideo}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 bg-white dark:bg-zinc-900 rounded-lg text-xs font-semibold hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="video-detail-prev-btn"
              title={prevVideoTitle
                ? `← ${prevVideoTitle}`
                : (lang === "fr" ? "Vidéo précédente" : "Previous video")}
            >
              <ArrowLeft size={14} weight="bold" />
              {lang === "fr" ? "Précédent" : "Previous"}
            </button>
            {videoNavLabel && (
              <span
                className="text-[11px] text-zinc-500 tabular-nums"
                data-testid="video-detail-nav-label"
              >
                {videoNavLabel}
              </span>
            )}
            <button
              type="button"
              onClick={goToNextVideo || undefined}
              disabled={!goToNextVideo}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 bg-white dark:bg-zinc-900 rounded-lg text-xs font-semibold hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="video-detail-next-btn"
              title={nextVideoTitle
                ? `${nextVideoTitle} →`
                : (lang === "fr" ? "Vidéo suivante" : "Next video")}
            >
              {lang === "fr" ? "Suivant" : "Next"}
              <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        )}
        </div>
        {/* Ligne 2 : informations sur le fichier */}
        <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          {editMode ? (
            <div className="space-y-2" data-testid="video-edit-form">
              <input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="w-full text-xl font-semibold font-['Outfit'] bg-transparent border-b-2 border-[#002FA7] outline-none py-1" data-testid="edit-title-input" />
              <div className="flex gap-2 items-start flex-wrap">
                {/* Vincent B644 (Eric 2026-08-01 SPEC STRICK PROD) — Ajout du
                    sélecteur Thème dans le formulaire d'édition. Avant on avait
                    Catégorie + Parcours mais PAS Thème → Vincent voyait la valeur
                    training_theme dans les chips (mode view) mais ne pouvait pas
                    la modifier (mode edit). Il voulait les 3 mêmes données dans
                    les 2 modes. */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-[10px] text-zinc-500 font-medium">
                    {lang === "fr" ? "Thème" : "Theme"}
                  </label>
                  <select
                    value={editTheme || ""}
                    onChange={(e) => setEditTheme && setEditTheme(e.target.value)}
                    className="text-sm border rounded px-2 py-1 min-w-[160px]"
                    data-testid="edit-theme-select"
                  >
                    {/* Tâche 1157 — Thèmes par ordre alphabétique. */}
                    {[
                      { value: "general", label: lang === "fr" ? "Général" : "General" },
                      { value: "sales", label: lang === "fr" ? "Vente" : "Sales" },
                      { value: "procedures", label: lang === "fr" ? "Procédures" : "Procedures" },
                      { value: "compliance", label: lang === "fr" ? "Conformité" : "Compliance" },
                      { value: "product", label: lang === "fr" ? "Produit" : "Product" },
                      { value: "onboarding", label: lang === "fr" ? "Intégration" : "Onboarding" },
                    ]
                      .sort((a, b) => a.label.localeCompare(b.label, "fr", { sensitivity: "base" }))
                      .map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                  </select>
                </div>
                {/* Vincent (2026-02) bug #524 — Menu Catégories dynamique
                    remplaçant l'ancien dropdown codé en dur
                    "General/Onboarding/Investissement/…". La liste vient de
                    /api/content-categories (menu Catégories de vidéos). */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-[10px] text-zinc-500 font-medium">
                    {lang === "fr" ? "Catégorie" : "Category"}
                  </label>
                  <select
                    value={editCategoryId || ""}
                    onChange={(e) => setEditCategoryId && setEditCategoryId(e.target.value)}
                    className="text-sm border rounded px-2 py-1 min-w-[180px]"
                    data-testid="edit-category-select"
                  >
                    <option value="">
                      {lang === "fr" ? "— Aucune catégorie —" : "— No category —"}
                    </option>
                    {/* Tâche 1157 — Catégories par ordre alphabétique. */}
                    {[...categories]
                      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "fr", { sensitivity: "base" }))
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                  </select>
                </div>
                {/* Vincent (2026-02) bug #525 — Menu Parcours (multi-select)
                    remplaçant l'ancien dropdown "Niveau 1 — Tous / Niveau 2 …".
                    Combine parcours d'intégration et d'apprentissage
                    (/api/pathways). Sauvegarde via PATCH /api/pathways/{id}. */}
                <div className="flex flex-col gap-0.5 relative">
                  <label className="text-[10px] text-zinc-500 font-medium">
                    {lang === "fr" ? "Parcours associés" : "Assigned pathways"}
                  </label>
                  <button
                    type="button"
                    onClick={() => setPathwayMenuOpen((v) => !v)}
                    className="text-sm border rounded px-2 py-1 min-w-[200px] text-left bg-white hover:bg-zinc-50"
                    data-testid="edit-pathways-toggle"
                  >
                    {selectedCount > 0
                      ? (lang === "fr"
                          ? `${selectedCount} parcours sélectionné${selectedCount > 1 ? "s" : ""}`
                          : `${selectedCount} pathway${selectedCount > 1 ? "s" : ""} selected`)
                      : (lang === "fr" ? "— Aucun parcours —" : "— No pathway —")}
                    <span className="ml-1 text-zinc-400">▾</span>
                  </button>
                  {pathwayMenuOpen && (
                    <div
                      className="absolute z-50 top-full left-0 mt-1 w-[320px] max-h-[320px] overflow-y-auto bg-white border border-zinc-200 rounded-lg shadow-lg p-2"
                      data-testid="edit-pathways-menu"
                    >
                      {/* Tâche 1164 — Champ de recherche : à trente parcours,
                          dérouler trois familles n'est plus praticable. */}
                      <input
                        value={pathwayQuery}
                        onChange={(e) => setPathwayQuery(e.target.value)}
                        placeholder={lang === "fr" ? "Chercher un parcours…" : "Search a pathway…"}
                        className="w-full mb-1 px-2 py-1 text-xs border border-zinc-300 rounded"
                        data-testid="edit-pathways-search"
                      />
                      {onboardingPaths.length === 0 && learningPaths.length === 0
                        && weeklyPaths.length === 0 && (
                        <p className="text-xs text-zinc-500 p-2" data-testid="edit-pathways-empty">
                          {pathwayQuery.trim()
                            ? (lang === "fr" ? `Aucun parcours ne contient « ${pathwayQuery.trim()} ».` : `No pathway matches "${pathwayQuery.trim()}".`)
                            : (lang === "fr" ? "Aucun parcours dans cette organisation." : "No pathway in this organisation.")}
                        </p>
                      )}
                      {[
                        // Tâche 1168 — « Récents » en tête : les fichiers
                        // arrivent par lots et se rattachent au même parcours.
                        { key: "recent", list: recentPaths, accent: "text-zinc-600", hover: "hover:bg-zinc-100", box: "accent-zinc-700", label: lang === "fr" ? "Utilisés récemment" : "Recently used" },
                        { key: "onboarding", list: onboardingPaths, accent: "text-violet-600", hover: "hover:bg-violet-50", box: "accent-violet-600", label: lang === "fr" ? "Parcours d'intégration" : "Onboarding pathways" },
                        { key: "learning", list: learningPaths, accent: "text-teal-600", hover: "hover:bg-teal-50", box: "accent-emerald-600", label: lang === "fr" ? "Parcours d'apprentissage" : "Learning pathways" },
                        { key: "weekly", list: weeklyPaths, accent: "text-amber-600", hover: "hover:bg-amber-50", box: "accent-amber-600", label: lang === "fr" ? "Formations hebdomadaires" : "Weekly trainings" },
                      ].map((group) => {
                        if (group.list.length === 0) return null;
                        // Une recherche en cours déplie tout : chercher dans
                        // une famille repliée ne montrerait aucun résultat.
                        const collapsed = !pathwayQuery.trim()
                          && collapsedPathGroups.has(group.key);
                        const chosen = group.list.filter((p) => editSelectedPathwayIds?.has(p.id)).length;
                        return (
                          <div key={group.key}>
                            {/* Tâche 1157 — Triangle pour ouvrir ou fermer la
                                famille. Le compte des parcours cochés reste
                                visible même replié, sinon replier ferait
                                perdre l'information. */}
                            <button
                              type="button"
                              onClick={() => togglePathGroup(group.key)}
                              className={`w-full flex items-center gap-1 text-[10px] uppercase tracking-wider ${group.accent} font-semibold px-2 pt-2 pb-0.5 hover:bg-zinc-50 rounded`}
                              data-testid={`edit-pathway-group-toggle-${group.key}`}
                            >
                              {collapsed ? <CaretRight size={10} /> : <CaretDown size={10} />}
                              <span>{group.label}</span>
                              <span className="text-zinc-400 normal-case">({chosen}/{group.list.length})</span>
                            </button>
                            {!collapsed && group.list.map((p) => {
                              const checked = editSelectedPathwayIds?.has(p.id);
                              return (
                                <label
                                  key={`${group.key}-${p.id}`}
                                  className={`flex items-center gap-2 px-2 py-1.5 rounded ${group.hover} cursor-pointer text-xs`}
                                  data-testid={`edit-pathway-option-${group.key}-${p.id}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!checked}
                                    onChange={() => togglePathway(p.id)}
                                    className={`h-3.5 w-3.5 ${group.box}`}
                                  />
                                  <span className="flex-1 truncate">{p.name}</span>
                                </label>
                              );
                            })}
                          </div>
                        );
                      })}
                      <div className="flex justify-end mt-1 pt-1 border-t border-zinc-100">
                        <button
                          type="button"
                          onClick={() => setPathwayMenuOpen(false)}
                          className="text-[11px] text-zinc-500 hover:text-zinc-800 px-2 py-1"
                          data-testid="edit-pathways-menu-close"
                        >
                          {lang === "fr" ? "Fermer" : "Close"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <button onClick={saveEdit} disabled={editSaving} className="text-xs bg-[#002FA7] text-white px-3 py-1 rounded font-medium self-end" data-testid="save-edit-btn">
                  {editSaving ? "..." : tr("Sauvegarder", "Save")}
                </button>
                {hasEditChanges && !editSaving && (
                  <span
                    className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 self-end inline-flex items-center gap-1"
                    data-testid="edit-modified-badge"
                    title={lang === "fr" ? "Des modifications non sauvegardées sont détectées" : "Unsaved changes detected"}
                  >
                    • {lang === "fr" ? "Modifié" : "Modified"}
                  </span>
                )}
                <button onClick={() => setEditMode(false)} className="text-xs text-zinc-500 px-2 self-end" data-testid="cancel-edit-btn">Annuler</button>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <label className="text-xs text-zinc-500 font-medium whitespace-nowrap">Lien Dropbox :</label>
                <input
                  value={editDropbox}
                  onChange={e => setEditDropbox(e.target.value)}
                  placeholder="https://www.dropbox.com/scl/fi/.../video.mp4?rlkey=..."
                  className="flex-1 text-xs border border-zinc-200 rounded px-2 py-1 bg-white outline-none focus:border-[#002FA7]"
                  data-testid="edit-dropbox-input"
                />
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap" data-testid="video-detail-title-row">
                {/* Vincent B712 (Eric 2026-02-11) — Cœur favori à gauche du titre.
                    Toggle POST/DELETE /api/favorites. Type "video" pour vidéo
                    ET text-doc (même collection côté backend, cf. B588).
                    Vincent B712+ (Eric 2026-02-11) — Couleur BLEUE si le
                    fichier est populaire dans l'org (>= 3 favoris), rose
                    sinon. Tooltip enrichi indique le nombre d'utilisateurs
                    de l'org qui l'ont favori. */}
                <button
                  type="button"
                  onClick={toggleFavorite}
                  disabled={favLoading}
                  className={`p-1 rounded transition-colors flex-shrink-0 ${
                    isFavorite
                      ? (favStats.is_popular
                          ? "text-[#002FA7] hover:text-[#001a5c]"
                          : "text-pink-500 hover:text-pink-600")
                      : "text-zinc-300 hover:text-pink-400"
                  } disabled:opacity-50`}
                  data-testid="video-detail-favorite-btn"
                  data-favorite-popular={favStats.is_popular ? "true" : "false"}
                  title={(() => {
                    const n = favStats.count || 0;
                    if (isFavorite && favStats.is_popular) {
                      return lang === "fr"
                        ? `⭐ Contenu populaire — ${n} personne${n > 1 ? "s" : ""} de votre organisation ${n > 1 ? "l'ont" : "l'a"} mis en favori`
                        : `⭐ Popular content — favorited by ${n} member${n > 1 ? "s" : ""} of your organization`;
                    }
                    if (isFavorite) {
                      return lang === "fr"
                        ? (n > 1
                            ? `Ajouté à vos favoris — ${n} personnes au total dans l'organisation`
                            : "Ajouté à vos favoris (vous seul pour l'instant)")
                        : (n > 1
                            ? `In your favorites — ${n} people total in the organization`
                            : "In your favorites (only you so far)");
                    }
                    if (n > 0) {
                      return lang === "fr"
                        ? `${n} personne${n > 1 ? "s" : ""} de votre organisation ${n > 1 ? "l'ont" : "l'a"} mis en favori — Cliquez pour ajouter`
                        : `Favorited by ${n} member${n > 1 ? "s" : ""} — click to add`;
                    }
                    return lang === "fr" ? "Ajouter aux favoris" : "Add to favorites";
                  })()}
                  aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                  aria-pressed={isFavorite}
                >
                  <Heart size={20} weight={isFavorite ? "fill" : "regular"} />
                </button>
                <h1 className="text-xl font-semibold tracking-tight font-['Outfit'] truncate" data-testid="video-title">
                  {video.title}
                </h1>
                {/* Eric 2026-06-25 (iter125 fix testing) — Bouton crayon gaté
                    par canEdit : l'Apprenant ne voit pas le bouton d'édition. */}
                {canEdit && (
                  <button onClick={startEdit} className="p-1 hover:bg-zinc-100 rounded transition-colors" data-testid="edit-video-btn" title={lang === "fr" ? "Modifier le titre, le module, les tags et le lien Dropbox" : "Edit title, module, tags and Dropbox link"}>
                    <PencilSimple size={16} className="text-zinc-400" />
                  </button>
                )}
                {/* Tâche 1170 (Eric 2026-06-17) — Majuscules du TITRE selon la
                    typographie française : majuscule au premier mot
                    seulement, acronymes en majuscules, noms propres
                    capitalisés. Même garde-fou que le dictionnaire : seule
                    une correction de CASSE est acceptée. */}
                {canEdit && (
                  <button
                    onClick={fixTitleCase}
                    disabled={fixingTitle}
                    className="p-1 hover:bg-zinc-100 rounded transition-colors disabled:opacity-40"
                    data-testid="fix-title-case-btn"
                    title={lang === "fr"
                      ? "Corriger les majuscules du titre (IA)"
                      : "Fix title capitalisation (AI)"}
                  >
                    <TextAa size={16} className="text-violet-500" />
                  </button>
                )}
                {/* Vincent #258 (2026-02-06) → Eric 2026-06-25 (iter118 B3) —
                    migré vers canAccessAdmin (helper canonique). Bouton suppression
                    complète de la vidéo avec DOUBLE confirmation. Action
                    irréversible : supprime vidéo + Q&R + scripts + procédures +
                    concepts + transcripts + quiz attempts + completions. */}
                {canAccessAdmin(user) && (
                  <button
                    onClick={() => setShowDeleteConfirm(1)}
                    className="p-1 hover:bg-red-50 rounded transition-colors"
                    data-testid="delete-video-btn"
                    title={lang === "fr" ? "Effacer la vidéo et toutes ses connaissances" : "Delete video and all its knowledge"}
                  >
                    <Trash size={16} className="text-red-400 hover:text-red-600" />
                  </button>
                )}
                {/* Vincent B725 v4 (Eric 2026-02-14) — Étiquettes après corbeille
                    sur la MÊME ligne : date d'upload + Terminé ✓ + durée
                    visionnée + score quiz. Auparavant ces infos étaient
                    éparpillées sur 2-3 lignes en dessous. */}
                {video.created_at && (
                  <span
                    className="text-[11px] px-2 py-1 rounded border border-zinc-200 bg-zinc-50 text-zinc-700 font-medium whitespace-nowrap inline-flex items-center gap-1 ml-1"
                    data-testid="video-detail-header-upload-date"
                    title={lang === "fr" ? `Uploadé le ${new Date(video.created_at).toLocaleString("fr-CA")}` : `Uploaded ${new Date(video.created_at).toLocaleString("en-CA")}`}
                  >
                    {new Date(video.created_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { year: "numeric", month: "short", day: "numeric" })}
                  </span>
                )}
                {/* Tâche 1487 (Eric) — Un pipeline arrêté par l'utilisateur
                    sans autre connaissance que la synthèse affichait « En
                    cours » avec un spinner : il ne tourne plus, et promettre
                    du travail en cours est le pire des messages. */}
                <span
                  className={`text-[11px] px-2 py-1 rounded border font-semibold whitespace-nowrap inline-flex items-center gap-1 ${
                    video?.pipeline_partiel ? "bg-amber-50 text-amber-800 border-amber-400" :
                    isComplete ? "bg-teal-50 text-teal-700 border-teal-300" :
                    isFailed ? "bg-red-50 text-red-800 border-red-400 font-bold" :
                    isPaused ? "bg-amber-50 text-amber-800 border-amber-400" :
                    "bg-amber-50 text-amber-700 border-amber-200"
                  }`}
                  data-testid="video-detail-header-completed-tag"
                  title={video?.pipeline_partiel
                    ? (lang === "fr"
                      ? `Pipeline arrêté par ${video?.pipeline_conclu_par || "un utilisateur"} — il manque : ${(video?.pipeline_manquants || []).join(", ") || "tout"}`
                      : `Pipeline stopped by ${video?.pipeline_conclu_par || "a user"} — missing: ${(video?.pipeline_manquants || []).join(", ") || "everything"}`)
                    : undefined}
                >
                  {video?.pipeline_partiel ? <Warning size={11} weight="fill" /> :
                   isComplete ? <CheckCircle size={11} weight="fill" /> :
                   isFailed ? <Warning size={11} /> :
                   isPaused ? <Warning size={11} weight="fill" /> :
                   <CircleNotch size={11} className="animate-spin" />}
                  {video?.pipeline_partiel ? (lang === "fr" ? "Partiel" : "Partial") :
                   isComplete ? (lang === "fr" ? "Terminé" : "Complete") :
                   isFailed ? (lang === "fr" ? "Échec" : "Failed") :
                   isPaused ? (lang === "fr" ? "En pause" : "Paused") :
                   (lang === "fr" ? "En cours" : "Processing")}
                </span>
                {data?.my_progress && (data.my_progress.watched_seconds || 0) > 0 && (
                  <span
                    className={`text-[11px] px-2 py-1 rounded border font-semibold whitespace-nowrap inline-flex items-center gap-1 ${
                      data.my_progress.watched
                        ? "border-teal-300 bg-teal-50 text-teal-700"
                        : "border-violet-200 bg-violet-50 text-violet-700"
                    }`}
                    data-testid="video-detail-header-watch-badge"
                    title={lang === "fr" ? "Durée que vous avez visionnée" : "Duration you watched"}
                  >
                    {data.my_progress.watched && <CheckCircle size={11} weight="fill" />}
                    {(() => {
                      const total = Math.round(Number(data.my_progress.watched_seconds) || 0);
                      const h = Math.floor(total / 3600);
                      const m = Math.floor((total % 3600) / 60);
                      const rs = total % 60;
                      const parts = [];
                      if (h > 0) parts.push(`${h} h`);
                      if (m > 0) parts.push(`${m} min`);
                      if (rs > 0 || parts.length === 0) parts.push(`${rs} s`);
                      return parts.join(" ");
                    })()} ({data.my_progress.watch_pct}%)
                  </span>
                )}
                {data?.my_progress && data.my_progress.quiz_score !== null && data.my_progress.quiz_score !== undefined && (
                  <span
                    className={`text-[11px] px-2 py-1 rounded border font-semibold whitespace-nowrap inline-flex items-center gap-1 ${
                      data.my_progress.quiz_passed ? "bg-teal-50 text-teal-700 border-teal-300" : "bg-violet-50 text-violet-700 border-violet-200"
                    }`}
                    data-testid="video-detail-header-quiz-score"
                    title={lang === "fr" ? "Votre meilleur score au quiz" : "Your best quiz score"}
                  >
                    {data.my_progress.quiz_passed && <CheckCircle size={11} weight="fill" />}
                    Quiz ({data.my_progress.quiz_score}%)
                  </span>
                )}
              </div>
              <div className="flex items-center flex-wrap gap-2 text-sm text-zinc-500 mt-1" data-testid="video-detail-metadata-row">
                {/* Vincent B637 (Eric 2026-07-31 SPEC STRICK PROD, fiche vidéo) —
                    Auparavant on affichait uniquement `video.training_theme`
                    (valeur technique EN comme "sales", "onboarding"), non stylisé.
                    Nouveau : rangée de 4 chips FR + icônes Phosphor, exactement
                    comme la ligne d'édition inline de FileExplorerPage
                    (Thème + Catégorie + Chemin + Étiquettes). Chip masquée si
                    valeur vide (pas de "—" moche). */}
                {(() => {
                  const themeLabel = trainingThemeLabel(video.training_theme, lang);
                  const catName = video.category_id
                    ? (categories || []).find((c) => c.id === video.category_id)?.name || null
                    : null;
                  const modulePath = video.module_name || null;
                  const tagsArr = Array.isArray(video.tags) ? video.tags.filter(Boolean) : [];
                  // Vincent B644 (Eric 2026-08-01 SPEC STRICK PROD) — Résout les
                  // parcours (intégration + apprentissage) associés à cette vidéo
                  // pour l'afficher en chip. Utilise la même logique que
                  // `originalPathwayIds` — un parcours est associé si
                  // `video_ids.includes(videoId)`.
                  const assignedPathways = (allPathways || []).filter(
                    (p) => Array.isArray(p.video_ids) && p.video_ids.includes(videoId)
                  );
                  const chipCls = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-50 border border-zinc-200 text-xs text-zinc-700";
                  return (
                    <>
                      {themeLabel && (
                        <span className={chipCls} data-testid="video-meta-theme" title={`${tr("Thème", "Theme")} : ${themeLabel}`}>
                          <BookOpen size={12} className="text-zinc-500" />
                          <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{tr("Thème", "Theme")}</span>
                          <span className="text-zinc-700">{themeLabel}</span>
                        </span>
                      )}
                      {/* Tâche 1466 (Eric 2026-06) — Sur un fichier texte, la
                          chip était MASQUÉE quand aucune catégorie n'était
                          posée : rien n'indiquait qu'on pouvait en mettre une,
                          et on croyait la fonction absente. Elle est désormais
                          toujours là, avec « (aucune) », comme « Parcours ». */}
                      {(catName || isTextDoc) && (
                        <span className={chipCls} data-testid="video-meta-category" title={catName ? `${tr("Catégorie", "Category")} : ${catName}` : tr("Aucune catégorie — modifiez la fiche pour en choisir une.", "No category — edit the record to pick one.")}>
                          <FolderSimple size={12} className="text-zinc-500" />
                          <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{tr("Catégorie", "Category")}</span>
                          <span className="text-zinc-700">
                            {catName || <span className="text-zinc-400 italic">{tr("(aucune)", "(none)")}</span>}
                          </span>
                        </span>
                      )}
                      {/* Vincent B647 (Eric 2026-08-05 SPEC STRICK PROD) — La
                          chip « Parcours » DOIT toujours être visible en mode
                          view (même quand aucun parcours n'est associé) pour
                          rester symétrique avec le formulaire d'édition. On
                          affiche « (aucun) » dans ce cas. */}
                      <span className={chipCls} data-testid="video-meta-pathways" title={assignedPathways.length > 0 ? `${tr("Parcours", "Pathways")} : ${assignedPathways.map((p) => p.name).join(", ")}` : tr("Aucun parcours associé", "No pathway assigned")}>
                        <Path size={12} className="text-zinc-500" />
                        <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{tr("Parcours", "Pathways")}</span>
                        <span className="text-zinc-700 truncate max-w-[240px]">
                          {assignedPathways.length > 0 ? (
                            <>
                              {assignedPathways.slice(0, 2).map((p) => p.name).join(", ")}
                              {assignedPathways.length > 2 && <span className="text-zinc-400 ml-1">+{assignedPathways.length - 2}</span>}
                            </>
                          ) : (
                            <span className="text-zinc-400 italic">{tr("(aucun)", "(none)")}</span>
                          )}
                        </span>
                      </span>
                      {/* Tâche 1310 (Eric 2026-06-30) — La source canonique
                          était écrite en petit sous les boutons de l'onglet
                          Transcriptions (« 📌 Fusionnée = source canonique »).
                          C'est une information de la fiche : elle rejoint les
                          chips, juste après « Parcours », au même format. */}
                      {canonicalSourceLabel && (
                        <span className={chipCls} data-testid="video-meta-canonical-source" title={tr(
                          `Source canonique : ${canonicalSourceLabel} — c'est elle qu'utilisent les Q&R, les scripts, les procédures et la recherche.`,
                          `Canonical source: ${canonicalSourceLabel} — it is the one used by Q&A, scripts, procedures and search.`,
                        )}>
                          <FileText size={12} className="text-zinc-500" />
                          <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Source</span>
                          <span className="text-zinc-700">{canonicalSourceLabel}</span>
                        </span>
                      )}
                      {modulePath && (
                        <span className={chipCls} data-testid="video-meta-path" title={`${tr("Chemin", "Path")} : ${modulePath}`}>
                          <Tree size={12} className="text-zinc-500" />
                          <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{tr("Chemin", "Path")}</span>
                          <span className="text-zinc-700 truncate max-w-[280px]">{modulePath}</span>
                        </span>
                      )}
                      {/* Vincent B923 + B924 (Eric 2026-02-14 SPEC STRICK PROD) —
                          La chip « ÉTIQUETTES » devient cliquable pour
                          les éditeurs : ouvre `TagsManagerDialog` (ajout
                          manuel + suggestion auto + accept/reject). Pour
                          les non-éditeurs, reste un affichage passif. Le
                          `title=` liste TOUS les tags (B924 : survol =
                          liste complète, pas seulement 3+N). Le compteur
                          « +N » reste visible pour indiquer qu'il y en a
                          plus, mais le tooltip natif règle le survol.
                          Toujours rendu (même 0 tag) quand l'utilisateur
                          peut éditer, pour offrir le point d'entrée. */}
                      {(tagsArr.length > 0 || canEdit) && (
                        canEdit ? (
                          <button
                            type="button"
                            onClick={openTagsManager}
                            className={`${chipCls} hover:bg-teal-50 hover:border-teal-300 cursor-pointer transition-colors`}
                            data-testid="video-meta-tags"
                            data-tags-count={tagsArr.length}
                            title={tagsArr.length > 0
                              ? tr(
                                  `Étiquettes (${tagsArr.length}) : ${tagsArr.join(", ")}\n\nCliquer pour gérer (ajout manuel + suggestion auto).`,
                                  `Tags (${tagsArr.length}): ${tagsArr.join(", ")}\n\nClick to manage (manual add + auto suggestion).`,
                                )
                              : tr(
                                  "Aucune étiquette. Cliquer pour en ajouter (manuel ou suggestion auto).",
                                  "No tag yet. Click to add one (manual or auto suggestion).",
                                )}
                          >
                            <Tag size={12} className="text-zinc-500" />
                            <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{tr("Étiquettes", "Tags")}</span>
                            <span className="text-zinc-700">
                              {tagsArr.length > 0 ? (
                                <>
                                  {tagsArr.slice(0, 3).join(", ")}
                                  {tagsArr.length > 3 && <span className="text-zinc-400 ml-1">+{tagsArr.length - 3}</span>}
                                </>
                              ) : (
                                <span className="text-zinc-400 italic">{tr("(aucune)", "(none)")}</span>
                              )}
                            </span>
                          </button>
                        ) : (
                          <span
                            className={chipCls}
                            data-testid="video-meta-tags"
                            data-tags-count={tagsArr.length}
                            title={tr(
                              `Étiquettes (${tagsArr.length}) : ${tagsArr.join(", ")}`,
                              `Tags (${tagsArr.length}): ${tagsArr.join(", ")}`,
                            )}
                          >
                            <Tag size={12} className="text-zinc-500" />
                            <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{tr("Étiquettes", "Tags")}</span>
                            <span className="text-zinc-700">
                              {tagsArr.slice(0, 3).join(", ")}
                              {tagsArr.length > 3 && <span className="text-zinc-400 ml-1">+{tagsArr.length - 3}</span>}
                            </span>
                          </span>
                        )
                      )}
                      {/* Vincent B950 (Eric 2026-02-14 SPEC STRICK PROD) — Chip
                          « ✨ Suggérer tags » placé entre Étiquettes et Chemin
                          Dropbox. Fire un CustomEvent que TagSuggestionsBar
                          écoute pour déclencher regenerate() sans prop-drilling. */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.dispatchEvent(new CustomEvent("knoria:suggest-tags", {
                            detail: { videoId: video?.id },
                          }));
                        }}
                        className={`${chipCls} hover:bg-violet-50 hover:border-violet-300 cursor-pointer`}
                        data-testid="video-meta-suggest-tags-btn"
                        title={tr(
                          "Générer des tags suggérés (IA + TF-IDF) — les résultats apparaissent dans le bandeau ci-dessous",
                          "Generate suggested tags (AI + TF-IDF) — results appear in the bar below",
                        )}
                      >
                        <span className="text-xs">✨</span>
                        <span className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold">{tr("Suggérer tags", "Suggest tags")}</span>
                      </button>
                      {/* Vincent B647 (Eric 2026-08-05 SPEC STRICK PROD) — Chip
                          Dropbox visible en mode view, symétrique avec le champ
                          « Lien Dropbox » du formulaire d'édition. « (aucun) »
                          si vide. */}
                      <span className={chipCls} data-testid="video-meta-dropbox" title={video.dropbox_url ? `${tr("Chemin Dropbox", "Dropbox path")} : ${video.dropbox_url}` : tr("Aucun lien Dropbox défini", "No Dropbox link set")}>
                        <VideoCamera size={12} className="text-zinc-500" />
                        <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{tr("Chemin Dropbox", "Dropbox path")}</span>
                        <span className="text-zinc-700 truncate max-w-[240px]">
                          {video.dropbox_url ? (
                            <a
                              href={video.dropbox_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline decoration-dotted hover:text-teal-700"
                              onClick={(e) => e.stopPropagation()}
                              data-testid="video-meta-dropbox-link"
                            >
                              {video.dropbox_url.replace(/^https?:\/\//, "").slice(0, 40)}
                              {video.dropbox_url.length > 47 && "…"}
                            </a>
                          ) : (
                            <span className="text-zinc-400 italic">{tr("(aucun)", "(none)")}</span>
                          )}
                        </span>
                      </span>
                    </>
                  );
                })()}
                {/* Vincent B725 v4 (Eric 2026-02-14) — Date d'upload + statut
                    pipeline + progression (watched/quiz) ont été DÉPLACÉS
                    sur la ligne du titre (heart+filename+pencil+trash+…).
                    Ne pas remettre ici pour éviter la duplication.
                    Le bloc data?.my_progress d'origine (watch + quiz sous
                    le titre, Vincent B317/B601/B602/B615/B645/B646) est
                    retiré ci-dessous car intégré à la ligne du titre. */}
              </div>
              {/* Bloc data?.my_progress (Vincent #317/B601/B615/B646) et
                  duplicat de commentaire ci-dessous retirés — intégrés à la
                  ligne du titre (badges watch + quiz). Vincent B725 v4. */}
            </>
          )}
        </div>
        </div>
      </div>

      {/* Vincent B736 (Eric 2026-02-14) — Le panneau JargonTermsPanel a été
          DÉPLACÉ dans VideoDetailPage.js pour former une grille 2 colonnes
          50/50 avec la card « Fireflies déjà disponible ». Ne PLUS le rendre
          ici. */}

      {/* Vincent (Eric 2026-02-06) — Bandeau suggestion de parcours après pipeline */}
      {isComplete && isAdmin && pathwaySuggestions.length > 0 && !pathwayBannerDismissed && (
        <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-4 mb-4" data-testid="pathway-suggestions-banner">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1">
              <h3 className="font-semibold text-sm text-violet-900 flex items-center gap-2">
                🎯 {lang === "fr" ? "Suggestions de parcours pertinents" : "Suggested learning pathways"}
              </h3>
              <p className="text-xs text-violet-700 mt-0.5">
                {lang === "fr"
                  ? `Cette vidéo correspond à ${pathwaySuggestions.length} parcours existant${pathwaySuggestions.length > 1 ? "s" : ""}. Sélectionne ceux où l'ajouter :`
                  : `This video matches ${pathwaySuggestions.length} existing pathway${pathwaySuggestions.length > 1 ? "s" : ""}. Pick the ones to add it to:`}
              </p>
            </div>
            <button
              onClick={() => setPathwayBannerDismissed(true)}
              className="text-violet-400 hover:text-violet-700 text-xs"
              data-testid="pathway-suggestions-dismiss"
              title={lang === "fr" ? "Masquer ce bandeau" : "Dismiss"}
            >✕</button>
          </div>
          <div className="space-y-1.5 mb-3">
            {pathwaySuggestions.map(p => {
              const isChecked = pathwaySelected.has(p.id);
              return (
                <label key={p.id} className="flex items-start gap-2 p-2 rounded hover:bg-violet-100 cursor-pointer" data-testid={`pathway-suggestion-${p.id}`}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => {
                      setPathwaySelected(prev => {
                        const n = new Set(prev);
                        if (n.has(p.id)) n.delete(p.id); else n.add(p.id);
                        return n;
                      });
                    }}
                    className="mt-0.5 h-4 w-4 rounded border-violet-400 text-violet-600 focus:ring-violet-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-zinc-900">{p.name}</span>
                      <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-violet-100 text-violet-700">
                        {p.pathway_type === "onboarding" ? (lang === "fr" ? "Intégration" : "Onboarding") : p.pathway_type === "weekly" ? (lang === "fr" ? "Hebdomadaire" : "Weekly") : (lang === "fr" ? "Apprentissage" : "Learning")}
                      </span>
                      {!p.is_published && <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-zinc-200 text-zinc-600">{lang === "fr" ? "Brouillon" : "Draft"}</span>}
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      {(p.video_ids?.length || 0)} {lang === "fr" ? "vidéo(s)" : "video(s)"} · {lang === "fr" ? "Score" : "Score"} {p.score} · {(p.match_reasons || []).join(", ")}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setPathwayBannerDismissed(true)}
              className="text-xs px-3 py-1.5 rounded text-violet-600 hover:bg-violet-100"
              data-testid="pathway-suggestions-skip"
            >
              {lang === "fr" ? "Ignorer" : "Skip"}
            </button>
            <button
              disabled={pathwaySelected.size === 0 || pathwayAdding}
              onClick={async () => {
                if (pathwaySelected.size === 0 || pathwayAdding) return;
                setPathwayAdding(true);
                let added = 0;
                let failed = 0;
                for (const pid of pathwaySelected) {
                  try {
                    await axios.post(`${api}/api/pathways/${pid}/add-video`, { video_id: videoId }, { withCredentials: true });
                    added++;
                  } catch {
                    failed++;
                  }
                }
                toast.success(lang === "fr"
                  ? `Vidéo ajoutée à ${added} parcours${failed > 0 ? ` (${failed} échec)` : ""}`
                  : `Video added to ${added} pathway(s)${failed > 0 ? ` (${failed} failed)` : ""}`);
                setPathwayBannerDismissed(true);
                setPathwayAdding(false);
              }}
              className="text-xs px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="pathway-suggestions-add-btn"
            >
              {pathwayAdding
                ? (lang === "fr" ? "⏳ Ajout…" : "⏳ Adding…")
                : (lang === "fr" ? `Ajouter aux ${pathwaySelected.size} parcours sélectionnés` : `Add to ${pathwaySelected.size} selected pathway(s)`)}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default VideoDetailHeader;
